export const KIND_TEXT = 'text'
export const KIND_IMAGE = 'image'
export const KIND_GIF = 'gif'
export const KIND_STICKER = 'sticker'
export const KIND_FILE = 'file'
export const KIND_AUDIO = 'audio'

export const MAX_AUDIO_DURATION_MS = 120_000
export const MIN_VOICE_DURATION_MS = 1_000

export const BLOCKED_PLACEHOLDER =
  '[Contenuto multimediale bloccato dalle impostazioni di privacy]'

export const MAX_BETA_MEDIA_BYTES = 3 * 1024 * 1024
export const MAX_MEDIA_BYTES = MAX_BETA_MEDIA_BYTES
export const MAX_DOCUMENT_BYTES = 5 * 1024 * 1024
/** GIF animate (pass-through, no canvas): fino a 5 MB. */
export const MAX_GIF_BYTES = MAX_DOCUMENT_BYTES

export const DOCUMENT_TOO_LARGE_MSG =
  '⚠️ FILE TROPPO GRANDE: Il limite massimo di sicurezza per i documenti è 5MB.'

export const GIF_TOO_LARGE_MSG =
  '⚠️ GIF TROPPO GRANDE: Il limite massimo per le GIF animate è 5MB.'

export function maxBytesForKind(kind: string | null | undefined): number {
  const k = normalizeKind(kind)
  if (k === KIND_FILE) return MAX_DOCUMENT_BYTES
  if (k === KIND_GIF) return MAX_GIF_BYTES
  if (k === KIND_AUDIO) return MAX_MEDIA_BYTES
  return MAX_MEDIA_BYTES
}

export function isDocumentFile(file: File): boolean {
  const hint = kindFromMime(file.type || '')
  if (hint === KIND_GIF || /\.gif$/i.test(file.name)) return false
  if (hint === KIND_FILE) return true
  const name = file.name.toLowerCase()
  return /\.(pdf|docx?|txt|zip|rar|xlsx|pptx)$/.test(name)
}

export function isGifBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 6 &&
    bytes[0] === 0x47 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x38 &&
    (bytes[4] === 0x37 || bytes[4] === 0x39) &&
    bytes[5] === 0x61
  )
}

function looksLikeWebpBytes(bytes: Uint8Array): boolean {
  return (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  )
}

function looksLikeHeicBytes(bytes: Uint8Array): boolean {
  if (
    bytes.length < 12 ||
    bytes[4] !== 0x66 ||
    bytes[5] !== 0x74 ||
    bytes[6] !== 0x79 ||
    bytes[7] !== 0x70
  ) {
    return false
  }
  const brand = String.fromCharCode(bytes[8]!, bytes[9]!, bytes[10]!, bytes[11]!).toLowerCase()
  return (
    brand.includes('hei') ||
    brand.includes('mif1') ||
    brand.includes('msf1') ||
    brand.includes('hevc') ||
    brand.includes('heif')
  )
}

export function sniffMime(bytes: Uint8Array): string | null {
  if (!bytes.length) return null
  if (isGifBytes(bytes)) return 'image/gif'
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xd8) return 'image/jpeg'
  if (
    bytes.length >= 8 &&
    bytes[0] === 0x89 &&
    bytes[1] === 0x50 &&
    bytes[2] === 0x4e &&
    bytes[3] === 0x47
  ) {
    return 'image/png'
  }
  if (looksLikeWebpBytes(bytes)) return 'image/webp'
  if (looksLikeHeicBytes(bytes)) return 'image/heic'
  if (bytes.length >= 4 && bytes[0] === 0x25 && bytes[1] === 0x50 && bytes[2] === 0x44 && bytes[3] === 0x46) {
    return 'application/pdf'
  }
  if (
    bytes.length >= 8 &&
    bytes[4] === 0x66 &&
    bytes[5] === 0x74 &&
    bytes[6] === 0x79 &&
    bytes[7] === 0x70
  ) {
    return 'audio/mp4'
  }
  if (bytes.length >= 4 && bytes[0] === 0x4f && bytes[1] === 0x67 && bytes[2] === 0x67 && bytes[3] === 0x53) {
    return 'audio/ogg'
  }
  if (bytes.length >= 4 && bytes[0] === 0x1a && bytes[1] === 0x45 && bytes[2] === 0xdf && bytes[3] === 0xa3) {
    return 'audio/webm'
  }
  if (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x41 &&
    bytes[10] === 0x56 &&
    bytes[11] === 0x45
  ) {
    return 'audio/wav'
  }
  return null
}

export function isMediaKind(kind: string | null | undefined): boolean {
  const k = kind?.trim().toLowerCase() ?? ''
  return k === KIND_IMAGE || k === KIND_GIF || k === KIND_STICKER || k === KIND_FILE || k === KIND_AUDIO
}

export function normalizeKind(kind: string | null | undefined): string {
  const k = kind?.trim().toLowerCase() ?? ''
  if (k === KIND_IMAGE || k === KIND_GIF || k === KIND_STICKER || k === KIND_FILE || k === KIND_AUDIO) return k
  return KIND_TEXT
}

export function kindFromMime(mime: string | null | undefined): string {
  const m = mime?.trim().toLowerCase() ?? ''
  if (m === 'image/gif' || m.includes('gif')) return KIND_GIF
  if (m === 'image/webp') return KIND_STICKER
  if (m.startsWith('image/')) return KIND_IMAGE
  if (m.startsWith('audio/')) return KIND_AUDIO
  return KIND_FILE
}

export function mediaLabel(kind: string | null | undefined, fileName?: string): string {
  const k = normalizeKind(kind)
  if (k === KIND_IMAGE) return '🖼️ Foto'
  if (k === KIND_GIF) return 'GIF'
  if (k === KIND_STICKER) return 'Sticker'
  if (k === KIND_AUDIO) return '🎤 Vocale'
  if (k === KIND_FILE) return fileName ? `📎 ${fileName}` : '📎 Documento'
  return 'Media'
}

export function formatFileSize(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes < 0) return '—'
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export function documentDisplayLabel(fileName: string, byteLength: number): string {
  return `${fileName} [${formatFileSize(byteLength)}]`
}
